import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "../lib/api";

export default function NotificationsBell({ authed }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);

  const load = async () => {
    if (!authed) return;
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      setData(data);
    } catch (e) { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { if (authed) { load(); const id = setInterval(load, 60000); return () => clearInterval(id); } }, [authed]);

  useEffect(() => {
    const handler = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!authed) return null;

  const markRead = async (id) => {
    setData((d) => ({ ...d, items: d.items.map((n) => n.id === id ? { ...n, read: true } : n), unread: Math.max(0, d.unread - 1) }));
    try { await api.put(`/notifications/${id}/read`); } catch (e) { void e; }
  };

  const markAllRead = async () => {
    setData((d) => ({ ...d, items: d.items.map((n) => ({ ...n, read: true })), unread: 0 }));
    try { await api.put("/notifications/read-all"); } catch (e) { void e; }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        data-testid="notifications-bell"
        onClick={() => { setOpen((o) => !o); if (!open) load(); }}
        aria-label="Notifications"
        className="relative text-[#A8A8A8] hover:text-[#4ADE80] transition-colors p-1"
      >
        <Bell className="h-[18px] w-[18px]" strokeWidth={1.5} />
        {data.unread > 0 && (
          <span data-testid="notifications-badge" className="absolute -top-0.5 -right-0.5 bg-[#4ADE80] text-[#0F0F10] text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            data-testid="notifications-panel"
            className="absolute right-0 mt-3 w-[360px] max-w-[92vw] bg-[#171717] border border-white/10 shadow-2xl rounded-lg overflow-hidden z-50"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#F5F5F5]">Notifications</div>
              {data.unread > 0 && (
                <button data-testid="notifications-mark-all-read" onClick={markAllRead} className="text-[10px] uppercase tracking-[0.14em] text-[#4ADE80] hover:text-[#6FE998] inline-flex items-center gap-1">
                  <Check className="h-3 w-3" /> Mark all
                </button>
              )}
            </div>
            <div className="max-h-[440px] overflow-y-auto">
              {loading && <div className="p-6 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-[#4ADE80]" /></div>}
              {!loading && data.items.length === 0 && (
                <div className="p-8 text-center text-sm text-[#A8A8A8]">You&apos;re all caught up.</div>
              )}
              {!loading && data.items.map((n) => {
                const Content = (
                  <div className={`px-4 py-3 border-b border-white/5 hover:bg-white/[0.02] transition-colors ${n.read ? "opacity-70" : ""}`}>
                    <div className="flex items-start gap-3">
                      {!n.read && <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#4ADE80] shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] text-[#F5F5F5]">{n.title}</div>
                        {n.body && <div className="text-xs text-[#A8A8A8] mt-0.5 line-clamp-2">{n.body}</div>}
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[#7a7a7a] mt-1.5">{new Date(n.created_at).toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                );
                return n.link ? (
                  <Link key={n.id} to={n.link} data-testid={`notification-item-${n.id}`} onClick={() => { markRead(n.id); setOpen(false); }} className="block">
                    {Content}
                  </Link>
                ) : (
                  <button key={n.id} data-testid={`notification-item-${n.id}`} onClick={() => markRead(n.id)} className="w-full text-left">
                    {Content}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
